// SFX — every sound on this site, synthesised in the browser. Zero shipped bytes.
//
// y2kdotcom.xyz ships five mp3s (click, popup, gameboy, word-scramble, matrix-2). We are not
// shipping theirs and we are not shipping ours either: WebAudio makes all of it from oscillators
// and noise, so the whole sound design costs 0 KB of transfer and can never 404.
//
// What actually makes a click satisfying, measured off their files and general UI-audio practice:
//   1. It is SHORT. Their click is 0.648s of file but the transient inside it is ~20ms. Anything
//      longer than ~60ms stops feeling like a click and starts feeling like a noise.
//   2. Sharp attack, exponential decay. Linear decay sounds cheap.
//   3. Two layers — a high transient (the "tick") and a small low body (the "thock"). One alone
//      sounds thin or muddy respectively.
//   4. Tiny per-hit randomisation of pitch and level, or rapid repeats sound like a machine gun.
//
// Policy, deliberately: nothing plays until the visitor's first real gesture (browsers require it
// anyway, and surprise audio is hostile), the mute state persists, and a single call site can
// disable the lot. Everything is wrapped so a browser without WebAudio degrades to silence
// rather than to a crash.

const STORE_KEY = 'ic:sfx:muted';

let ctx = null;
let master = null;
let unlocked = false;
let muted = readMuted();
const listeners = new Set();

function readMuted() {
  try {
    return window.localStorage.getItem(STORE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function persistMuted(v) {
  try {
    window.localStorage.setItem(STORE_KEY, v ? '1' : '0');
  } catch (e) {
    /* private window: honour it for this session only */
  }
}

function audio() {
  if (ctx) return ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.30; // headroom: every voice below is written to peak near 1.0
    master.connect(ctx.destination);
  } catch (e) {
    ctx = null;
  }
  return ctx;
}

/**
 * Browsers refuse audio before a gesture, so the cold open is silent on a fresh load no matter
 * what we do. Rather than pretend otherwise: arm a one-shot capture-phase listener on the first
 * real gesture, and if the boot ran to completion while we were still locked, play the payoff
 * chime once on unlock. The ticks and beeps that were missed are gone, which is correct — nobody
 * wants a boot sequence replayed at them three seconds late.
 */
export function unlock() {
  const c = audio();
  if (!c) return false;
  if (c.state === 'suspended') c.resume().catch(() => {});
  const first = !unlocked;
  unlocked = true;
  if (first && missedReady) {
    missedReady = false;
    // One frame later, so the gesture's own click sound lands first.
    setTimeout(() => bootChime(), 90);
  }
  return true;
}

let missedReady = false;
let armed = false;

/** Install the first-gesture unlock. Idempotent, safe to call from anywhere. */
export function arm() {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  const go = () => {
    unlock();
    window.removeEventListener('pointerdown', go, true);
    window.removeEventListener('keydown', go, true);
    window.removeEventListener('touchstart', go, true);
  };
  window.addEventListener('pointerdown', go, true);
  window.addEventListener('keydown', go, true);
  window.addEventListener('touchstart', go, true);
}

/**
 * Install the whole interaction layer with one delegated listener.
 *
 * The alternative is threading an onClick through forty components, which nobody maintains and
 * which silently stops covering anything added later. A capture-phase listener on the document
 * covers every button, icon, menu item, tile and title bar that exists now or ever will.
 *
 * What it deliberately does NOT click on: text inputs and textareas (typing is not clicking),
 * anything inside an iframe (the emulators and the Wayback browser own their own audio), and
 * scroll surfaces. Returns a teardown.
 */
export function attach() {
  if (attached || typeof document === 'undefined') return () => {};
  attached = true;
  arm();

  const INTERACTIVE =
    'button, a, [role="button"], input[type="checkbox"], input[type="radio"], select, ' +
    '[class*="icon"], [class*="__tile"], [class*="menu"], [class*="btn"], [class*="tab"], ' +
    '.app__header, .header__button, [class*="start"], [class*="task"]';

  const isTyping = el => /^(input|textarea)$/i.test(el.tagName) && !/^(checkbox|radio|button|submit)$/i.test(el.type || '');

  const onDown = e => {
    const el = e.target && e.target.closest ? e.target.closest(INTERACTIVE) : null;
    if (!el || isTyping(e.target)) return;
    click();
  };

  // Hover is cheap but fires constantly, so it is throttled and only on real controls.
  let lastHover = 0;
  const onOver = e => {
    const el = e.target && e.target.closest ? e.target.closest('button, a, [role="button"], [class*="__tile"]') : null;
    if (!el) return;
    const now = Date.now();
    if (now - lastHover < 45) return;
    lastHover = now;
    hover();
  };

  // Typing anywhere real gets the terminal tick.
  const onKey = e => {
    if (!isTyping(e.target)) return;
    if (e.key && e.key.length === 1) tick();
    else if (e.key === 'Backspace' || e.key === 'Enter') tick();
  };

  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('pointerover', onOver, true);
  document.addEventListener('keydown', onKey, true);

  return () => {
    document.removeEventListener('pointerdown', onDown, true);
    document.removeEventListener('pointerover', onOver, true);
    document.removeEventListener('keydown', onKey, true);
    attached = false;
  };
}

let attached = false;

export function isMuted() {
  return muted;
}

export function setMuted(v) {
  muted = !!v;
  persistMuted(muted);
  listeners.forEach(fn => {
    try {
      fn(muted);
    } catch (e) {
      /* a bad listener must not break audio */
    }
  });
  return muted;
}

export function toggleMuted() {
  return setMuted(!muted);
}

export function onMuteChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function live() {
  if (muted || !unlocked) return null;
  const c = audio();
  if (!c || c.state !== 'running') return null;
  return c;
}

// ── synthesis primitives ─────────────────────────────────────────────────────

/** One oscillator with an exponential decay envelope. */
function tone(c, opts) {
  const {
    freq = 880,
    type = 'sine',
    dur = 0.08,
    gain = 0.5,
    at = 0,
    attack = 0.002,
    sweepTo = null,
    detune = 0,
  } = opts;

  const t0 = c.currentTime + at;
  const osc = c.createOscillator();
  const g = c.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (detune) osc.detune.setValueAtTime(detune, t0);
  if (sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, sweepTo), t0 + dur);

  // Exponential decay: the ramp cannot reach 0, so land on a floor and stop the node.
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
  return osc;
}

/** A burst of filtered noise — the transient half of a click. */
function noise(c, opts) {
  const { dur = 0.03, gain = 0.4, at = 0, type = 'highpass', freq = 2000, q = 0.7 } = opts;
  const t0 = c.currentTime + at;
  const frames = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, frames, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;

  const src = c.createBufferSource();
  src.buffer = buf;

  const filter = c.createBiquadFilter();
  filter.type = type;
  filter.frequency.setValueAtTime(freq, t0);
  filter.Q.setValueAtTime(q, t0);

  const g = c.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  src.connect(filter);
  filter.connect(g);
  g.connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
  return src;
}

/** ±cents of random pitch, so rapid repeats never sound identical. */
const vary = (freq, cents) => freq * Math.pow(2, ((Math.random() * 2 - 1) * cents) / 1200);

// ── the sounds ───────────────────────────────────────────────────────────────

/** The workhorse. ~25 ms: a high tick over a small body. */
export function click() {
  const c = live();
  if (!c) return;
  noise(c, { dur: 0.018, gain: 0.5, type: 'highpass', freq: vary(3200, 120) });
  tone(c, { freq: vary(1180, 90), type: 'triangle', dur: 0.028, gain: 0.30 });
  tone(c, { freq: vary(260, 60), type: 'sine', dur: 0.045, gain: 0.16 });
}

/** Quieter, higher, shorter. Fires a lot, so it has to stay out of the way. */
export function hover() {
  const c = live();
  if (!c) return;
  tone(c, { freq: vary(2050, 140), type: 'sine', dur: 0.016, gain: 0.10 });
}

/** A window opening: two tones rising. */
export function open() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 520, type: 'triangle', dur: 0.07, gain: 0.22 });
  tone(c, { freq: 780, type: 'triangle', dur: 0.10, gain: 0.20, at: 0.045 });
  noise(c, { dur: 0.02, gain: 0.16, type: 'bandpass', freq: 1800 });
}

/** A window closing: the same shape, falling. */
export function close() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 700, type: 'triangle', dur: 0.07, gain: 0.20 });
  tone(c, { freq: 430, type: 'triangle', dur: 0.10, gain: 0.18, at: 0.04 });
}

/** The XP-family error ding: a two-note chord with a touch of detune for beating. */
export function error() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 784, type: 'sine', dur: 0.34, gain: 0.34, attack: 0.004 });
  tone(c, { freq: 523.25, type: 'sine', dur: 0.42, gain: 0.30, attack: 0.004, detune: 6 });
  tone(c, { freq: 1046.5, type: 'sine', dur: 0.20, gain: 0.12, at: 0.02 });
}

/** A dialog arriving. Brighter and shorter than an error, no dread in it. */
export function popup() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 1174.7, type: 'sine', dur: 0.09, gain: 0.26 });
  tone(c, { freq: 1567.98, type: 'sine', dur: 0.16, gain: 0.20, at: 0.06 });
}

/** Per-character type tick, for terminals and the boot log. */
export function tick() {
  const c = live();
  if (!c) return;
  noise(c, { dur: 0.010, gain: 0.45, type: 'bandpass', freq: vary(2600, 200), q: 1.1 });
}

/** Money landing. Used when a dividend or a win is shown. */
export function coin() {
  const c = live();
  if (!c) return;
  const notes = [1046.5, 1318.5, 1568, 2093];
  notes.forEach((f, i) => tone(c, { freq: f, type: 'square', dur: 0.10, gain: 0.13, at: i * 0.055 }));
}

/**
 * The 56k handshake, compressed to about 2.6 seconds.
 * Not a sample: two DTMF-ish dial pairs, the carrier tones, and a band-limited noise screech that
 * resolves into the connect chirp. It is the single most nostalgic sound in the whole palette.
 */
export function dialup() {
  const c = live();
  if (!c) return;

  // Off-hook click, then dial tone.
  noise(c, { dur: 0.02, gain: 0.3, type: 'highpass', freq: 2000 });
  tone(c, { freq: 350, type: 'sine', dur: 0.45, gain: 0.14, at: 0.05 });
  tone(c, { freq: 440, type: 'sine', dur: 0.45, gain: 0.14, at: 0.05 });

  // Two dialled digits.
  [[697, 1209], [770, 1336]].forEach((pair, i) => {
    const at = 0.6 + i * 0.18;
    tone(c, { freq: pair[0], type: 'sine', dur: 0.1, gain: 0.16, at });
    tone(c, { freq: pair[1], type: 'sine', dur: 0.1, gain: 0.16, at });
  });

  // Carrier: the two answer tones, then the negotiation screech.
  tone(c, { freq: 2100, type: 'sine', dur: 0.5, gain: 0.13, at: 1.0 });
  tone(c, { freq: 1100, type: 'sine', dur: 0.35, gain: 0.11, at: 1.35 });
  noise(c, { dur: 0.55, gain: 0.16, type: 'bandpass', freq: 1400, q: 0.6, at: 1.6 });
  noise(c, { dur: 0.35, gain: 0.13, type: 'bandpass', freq: 2600, q: 0.5, at: 1.9 });
  tone(c, { freq: 1800, type: 'sawtooth', dur: 0.3, gain: 0.07, at: 1.75, sweepTo: 900 });

  // Connected.
  tone(c, { freq: 1568, type: 'sine', dur: 0.12, gain: 0.2, at: 2.35 });
  tone(c, { freq: 2093, type: 'sine', dur: 0.22, gain: 0.18, at: 2.45 });
}

/** The resolve at the end of the cold open. Warm, major, unhurried. */
export function bootChime() {
  const c = live();
  if (!c) return;
  const chord = [261.63, 392, 523.25, 659.25];
  chord.forEach((f, i) =>
    tone(c, { freq: f, type: 'sine', dur: 1.5 - i * 0.15, gain: 0.16, at: i * 0.075, attack: 0.06 }),
  );
  tone(c, { freq: 1046.5, type: 'triangle', dur: 0.9, gain: 0.07, at: 0.3, attack: 0.12 });
}

/** A CRT waking up. */
export function degauss() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 90, type: 'sine', dur: 0.5, gain: 0.3, sweepTo: 38, attack: 0.005 });
  noise(c, { dur: 0.2, gain: 0.1, type: 'lowpass', freq: 400 });
  tone(c, { freq: 15700, type: 'sine', dur: 0.6, gain: 0.012, at: 0.2, attack: 0.2 });
}

/** Single POST beep. */
export function beep() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 1000, type: 'square', dur: 0.12, gain: 0.16, attack: 0.001 });
}

/** Rising tone under a progress ramp. */
export function ramp() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 220, type: 'triangle', dur: 1.1, gain: 0.16, sweepTo: 660, attack: 0.05 });
}

/** The glitch-decode scramble. */
export function decode() {
  const c = live();
  if (!c) return;
  for (let i = 0; i < 7; i += 1) {
    noise(c, { dur: 0.03, gain: 0.30, type: 'bandpass', freq: 600 + Math.random() * 3200, q: 1.2, at: i * 0.045 });
  }
}

/**
 * The single hook the Boot component calls: `<Boot onStep={sfx.boot} />`.
 * Accepts a bare step name or `{ step }`, so either shape the boot agent ships will work.
 */
export function boot(stepOrObject) {
  const step = typeof stepOrObject === 'string' ? stepOrObject : (stepOrObject && stepOrObject.step) || '';
  switch (step) {
    case 'power':
      return degauss();
    case 'post':
      return beep();
    case 'log':
      return tick();
    case 'ramp':
      return ramp();
    case 'decode':
      return decode();
    case 'dialup':
      return dialup();
    case 'ready':
      // A cold load reaches 'ready' before any gesture exists, so remember it and pay it off on
      // unlock instead of dropping it silently.
      if (!live()) missedReady = !muted;
      return bootChime();
    default:
      return undefined;
  }
}

// ── the casino floor ─────────────────────────────────────────────────────────
//
// Everything below is the CASINO.exe palette. Same rules as the rest of this file: oscillators
// and noise only, short envelopes, exponential decays, every voice gated by `live()` so the mute
// flag and the pre-gesture lock hold. Zero shipped bytes.
//
// API — names, arguments, and the wall-clock length each one occupies:
//
//   casinoOpen()          ~2.5s  bell arpeggio (C5 E5 G5 C6) over a 2s floor-murmur bed with
//                                three slot dings sprinkled through it. Self-limiting: two calls
//                                inside 1.5s play once, so a React StrictMode double-mount or a
//                                double-click on the icon cannot stack it. If the context is
//                                still resuming from the opening gesture it retries once at
//                                180 ms and then gives up.
//   chip()                ~60ms  one clay chip landing.
//   chipStack()          ~190ms  three chips, staggered and randomised.
//   wheelSpin(ms, ticks)   ms+0.2s  decelerating ratchet of `ticks` clicks across `ms`,
//                                finished by a settle thunk. Cancels any spin still scheduled,
//                                so calling it twice never doubles up. ms clamps to
//                                [120, 6000] and ticks to [2, 64]. Returns nothing.
//   ballDrop()           ~300ms  4-6 bounces with shrinking gaps, then the pocket clack.
//   reelSpin(ms)           ms    18 Hz mechanical rattle that stops on its own. Cancels a
//                                previous rattle. ms clamps to [120, 6000].
//   reelStop()            ~90ms  one reel landing: low sine thunk plus a noise knock.
//   diceRoll()           ~520ms  6-8 irregular bursts.
//   win(size)                    size 'small' (~370ms, two ascending dings) or 'big' (~900ms,
//                                five-note rise plus a 12-16 ping coin shower). Anything other
//                                than 'big' is treated as small — the caller cannot make it
//                                silent by passing junk.
//   jackpot()              ~2s   rising sweep, a 22-ping shower, and a final major chord.
//   lose()               ~260ms  two descending low-passed squares. Soft on purpose: losing is
//                                already the punishment.
//   countdownTick()       ~35ms  quiet 1 kHz tick, one per second of the beacon wait.
//   countdownGo()        ~175ms  brighter double tick — the round landed.

/**
 * A long bed of band-passed pinkish noise: a room of people and machines two doors away.
 * `noise()` opens at full level, which reads as a cut rather than a room, so this one carries its
 * own attack and its own slow release.
 */
function murmur(c, opts) {
  const { dur = 2, gain = 0.055, at = 0, freq = 560, q = 0.7, attack = 0.4, release = 1.2 } = opts;
  const t0 = c.currentTime + at;
  const frames = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, frames, c.sampleRate);
  const data = buf.getChannelData(0);
  // One-pole lowpass over white noise: cheap pink-ish tilt, no 4-stage filter bank needed.
  let last = 0;
  for (let i = 0; i < frames; i += 1) {
    last = last * 0.86 + (Math.random() * 2 - 1) * 0.14;
    data[i] = last * 3.2;
  }

  const src = c.createBufferSource();
  src.buffer = buf;

  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(freq, t0);
  filter.Q.setValueAtTime(q, t0);

  const g = c.createGain();
  // The hold has to sit after the attack even when a caller asks for a bed shorter than its own
  // envelope, or the ramps arrive out of order and the bed clicks.
  const hold = Math.max(t0 + attack + 0.01, t0 + dur - release);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack);
  g.gain.setValueAtTime(Math.max(0.0002, gain), hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  src.connect(filter);
  filter.connect(g);
  g.connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
  return src;
}

/** A square with the top taken off. Buzzy, never biting. */
function buzz(c, opts) {
  const { freq = 200, dur = 0.13, gain = 0.16, at = 0, cutoff = 760 } = opts;
  const t0 = c.currentTime + at;
  const osc = c.createOscillator();
  const lp = c.createBiquadFilter();
  const g = c.createGain();

  osc.type = 'square';
  osc.frequency.setValueAtTime(freq, t0);
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(cutoff, t0);
  lp.Q.setValueAtTime(0.7, t0);

  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  osc.connect(lp);
  lp.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
  return osc;
}

/** One clay chip at a scheduled offset. The shared body of chip() and chipStack(). */
function chipAt(c, at) {
  noise(c, { dur: 0.008, gain: 0.45, type: 'highpass', freq: vary(4200, 150), at });
  tone(c, { freq: vary(2200, 100), type: 'triangle', dur: 0.030, gain: 0.20, at, attack: 0.001 });
  tone(c, { freq: vary(430, 70), type: 'sine', dur: 0.055, gain: 0.12, at });
}

/** Money falling: `count` short high pings scattered across `span` seconds from `at`. */
function coinShower(c, at, span, count) {
  for (let i = 0; i < count; i += 1) {
    const t = at + Math.random() * span;
    tone(c, { freq: vary(1900 + Math.random() * 1400, 120), type: 'square', dur: 0.05, gain: 0.055, at: t });
    tone(c, { freq: vary(3600, 200), type: 'sine', dur: 0.03, gain: 0.025, at: t + 0.008 });
  }
}

/**
 * Stop a set of scheduled voices dead. A node whose start time has not arrived yet and whose stop
 * time is now simply never plays, which is the cancel we want; a node that already finished
 * cannot be stopped twice, which is the throw we swallow.
 */
function stopVoices(voices) {
  for (let i = 0; i < voices.length; i += 1) {
    try {
      voices[i].stop();
    } catch (e) {
      /* already ended — nothing to cancel */
    }
  }
  voices.length = 0;
}

let lastCasinoOpen = 0;
const wheelVoices = [];
const reelVoices = [];

/** The whole opening fanfare, once the context is known to be running. */
function playCasinoOpen(c) {
  // A room, two doors away.
  murmur(c, { dur: 2.0, gain: 0.055, freq: 560, q: 0.7, attack: 0.4, release: 1.2 });

  // C5 E5 G5 C6, struck not swelled, each one a sine body with a detuned triangle partial over it
  // so the bells shimmer instead of sitting still.
  const bells = [523.25, 659.25, 783.99, 1046.5];
  for (let i = 0; i < bells.length; i += 1) {
    const f = bells[i];
    const at = i * 0.16;
    tone(c, { freq: f, type: 'sine', dur: 0.62 - i * 0.04, gain: 0.20, at, attack: 0.004 });
    tone(c, { freq: f * 2, type: 'triangle', dur: 0.34, gain: 0.055, at, attack: 0.004, detune: 6 });
    tone(c, { freq: f, type: 'sine', dur: 0.50, gain: 0.06, at: at + 0.01, attack: 0.006, detune: -8 });
  }

  // Three machines paying out somewhere behind you.
  for (let i = 0; i < 3; i += 1) {
    const at = 0.55 + i * 0.45 + Math.random() * 0.22;
    tone(c, { freq: vary(1760 + i * 220, 60), type: 'sine', dur: 0.22, gain: 0.07, at, attack: 0.003 });
    tone(c, { freq: vary(2640 + i * 180, 60), type: 'triangle', dur: 0.12, gain: 0.03, at, attack: 0.003 });
  }
}

/**
 * CASINO.exe opening. Plays at most once per open: the guard collapses a StrictMode double-mount
 * and an impatient double-click into one fanfare.
 */
export function casinoOpen() {
  if (muted) return;
  const now = Date.now();
  if (now - lastCasinoOpen < 1500) return;
  lastCasinoOpen = now;

  const c = live();
  if (c) {
    playCasinoOpen(c);
    return;
  }
  // The click that opened the program is the same gesture that unlocks audio, and resume() is
  // asynchronous — so the context can still be 'suspended' at mount. One bounded retry, then the
  // fanfare is genuinely gone, which is better than a chime arriving a second late.
  unlock();
  setTimeout(() => {
    const ready = live();
    if (ready) playCasinoOpen(ready);
  }, 180);
}

/** One chip on felt: an 8 ms burst, a 2.2 kHz tick, and a little clay body under it. */
export function chip() {
  const c = live();
  if (!c) return;
  chipAt(c, 0);
}

/** Three chips pushed forward together. */
export function chipStack() {
  const c = live();
  if (!c) return;
  chipAt(c, 0);
  chipAt(c, 0.045 + Math.random() * 0.02);
  chipAt(c, 0.100 + Math.random() * 0.03);
}

/**
 * A wheel losing momentum: `ticks` clicks over `ms`, spaced by t = ms * (i/n)^0.62 so every gap
 * is wider than the one before it, then a soft thunk as it settles. Any spin still scheduled is
 * cancelled first, so a cabinet that re-triggers mid-spin gets one wheel, not two.
 */
export function wheelSpin(ms, ticks) {
  stopVoices(wheelVoices);
  const c = live();
  if (!c) return;

  const total = Math.min(6, Math.max(0.12, (Number(ms) || 700) / 1000));
  const n = Math.min(64, Math.max(2, Math.floor(Number(ticks) || 6)));

  for (let i = 0; i < n; i += 1) {
    const at = total * Math.pow(i / n, 0.62);
    wheelVoices.push(noise(c, { dur: 0.012, gain: 0.30, type: 'bandpass', freq: vary(2800, 150), q: 1.4, at }));
    wheelVoices.push(tone(c, { freq: vary(1500, 120), type: 'square', dur: 0.016, gain: 0.09, at }));
  }

  const settle = total + 0.05;
  wheelVoices.push(tone(c, { freq: 150, type: 'sine', dur: 0.16, gain: 0.22, at: settle, sweepTo: 95 }));
  wheelVoices.push(noise(c, { dur: 0.05, gain: 0.16, type: 'lowpass', freq: 700, at: settle }));
}

/** The roulette ball: a handful of bounces closing up, then the pocket. */
export function ballDrop() {
  const c = live();
  if (!c) return;
  const bounces = 4 + Math.floor(Math.random() * 3);
  let at = 0;
  let gap = 0.11;
  for (let i = 0; i < bounces; i += 1) {
    noise(c, { dur: 0.012, gain: 0.30 - i * 0.03, type: 'bandpass', freq: vary(3000, 200), q: 1.6, at });
    tone(c, { freq: vary(900 - i * 60, 120), type: 'triangle', dur: 0.030, gain: 0.14, at });
    at += gap;
    gap *= 0.62;
  }
  noise(c, { dur: 0.030, gain: 0.34, type: 'highpass', freq: 2200, at });
  tone(c, { freq: 220, type: 'sine', dur: 0.10, gain: 0.20, at, sweepTo: 140 });
}

/** Slot reels turning: filtered noise pulses at 18 Hz, stopping on their own after `ms`. */
export function reelSpin(ms) {
  stopVoices(reelVoices);
  const c = live();
  if (!c) return;

  const total = Math.min(6, Math.max(0.12, (Number(ms) || 900) / 1000));
  const step = 1 / 18;
  for (let at = 0; at < total; at += step) {
    reelVoices.push(noise(c, { dur: 0.018, gain: 0.16, type: 'bandpass', freq: vary(1500, 180), q: 2.2, at }));
    reelVoices.push(tone(c, { freq: vary(210, 100), type: 'square', dur: 0.014, gain: 0.05, at }));
  }
}

/** One reel landing. Called once per reel, so it has to be solid and short. */
export function reelStop() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 190, type: 'sine', dur: 0.09, gain: 0.32, sweepTo: 92, attack: 0.002 });
  noise(c, { dur: 0.045, gain: 0.26, type: 'lowpass', freq: 900, q: 0.8 });
  tone(c, { freq: vary(1400, 80), type: 'triangle', dur: 0.022, gain: 0.10 });
}

/** Dice in a cup: irregular on purpose — even spacing sounds like a drum machine. */
export function diceRoll() {
  const c = live();
  if (!c) return;
  const bursts = 6 + Math.floor(Math.random() * 3);
  let at = 0;
  for (let i = 0; i < bursts; i += 1) {
    noise(c, { dur: 0.022, gain: 0.30, type: 'bandpass', freq: vary(2100, 320), q: 1.1, at });
    tone(c, { freq: vary(620, 200), type: 'triangle', dur: 0.026, gain: 0.09, at });
    at += 0.030 + Math.random() * 0.034;
  }
}

/**
 * A win. 'big' gets the five-note rise and a shower of coins; anything else — including a caller
 * that passes nothing — gets the two-ding version, because a win should never land silent.
 */
export function win(size) {
  const c = live();
  if (!c) return;

  if (size === 'big') {
    const rise = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    for (let i = 0; i < rise.length; i += 1) {
      tone(c, { freq: rise[i], type: 'sine', dur: 0.34, gain: 0.19, at: i * 0.085, attack: 0.003 });
    }
    coinShower(c, 0.20, 0.70, 12 + Math.floor(Math.random() * 5));
    return;
  }

  tone(c, { freq: 987.77, type: 'sine', dur: 0.16, gain: 0.22, attack: 0.003 });
  tone(c, { freq: 1318.5, type: 'sine', dur: 0.26, gain: 0.20, at: 0.11, attack: 0.003 });
}

/** The one that makes people look up. Sweep, coins, then the chord that says keep it. */
export function jackpot() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 220, type: 'sawtooth', dur: 0.85, gain: 0.10, sweepTo: 1760, attack: 0.04 });
  tone(c, { freq: 330, type: 'triangle', dur: 0.85, gain: 0.07, sweepTo: 2640, attack: 0.06 });
  coinShower(c, 0.15, 1.15, 22);
  const chord = [523.25, 659.25, 783.99, 1046.5];
  for (let i = 0; i < chord.length; i += 1) {
    tone(c, { freq: chord[i], type: 'sine', dur: 0.75, gain: 0.17, at: 1.25 + i * 0.02, attack: 0.006 });
  }
  tone(c, { freq: 1568, type: 'triangle', dur: 0.55, gain: 0.07, at: 1.30, attack: 0.02 });
}

/** A loss, said quietly. Two notes down, low-passed, gone in a quarter second. */
export function lose() {
  const c = live();
  if (!c) return;
  buzz(c, { freq: 196, dur: 0.13, gain: 0.16, cutoff: 760 });
  buzz(c, { freq: 146.83, dur: 0.16, gain: 0.15, at: 0.10, cutoff: 620 });
}

/** One second of the beacon wait. Quiet enough to sit under a conversation. */
export function countdownTick() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 1000, type: 'sine', dur: 0.035, gain: 0.07, attack: 0.001 });
}

/** The round landed. */
export function countdownGo() {
  const c = live();
  if (!c) return;
  tone(c, { freq: 1318.5, type: 'sine', dur: 0.06, gain: 0.16, attack: 0.001 });
  tone(c, { freq: 1975.53, type: 'sine', dur: 0.10, gain: 0.14, at: 0.075, attack: 0.001 });
}

const sfx = {
  unlock,
  arm,
  attach,
  isMuted,
  setMuted,
  toggleMuted,
  onMuteChange,
  click,
  hover,
  open,
  close,
  error,
  popup,
  tick,
  coin,
  dialup,
  bootChime,
  degauss,
  beep,
  ramp,
  decode,
  boot,
  casinoOpen,
  chip,
  chipStack,
  wheelSpin,
  ballDrop,
  reelSpin,
  reelStop,
  diceRoll,
  win,
  jackpot,
  lose,
  countdownTick,
  countdownGo,
};

export default sfx;
